import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Building2, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import useSeo from "@/hooks/useSeo";

const PRODUCTS = ["Working Capital", "Asset Finance", "Commercial Mortgage", "Invoice Finance", "Growth Capital", "Bridging Finance"];

export default function LenderSignup() {
  useSeo({
    title: "Apply to Join the SwiftCash Lender Panel",
    description: "Register your lender firm to bid on live UK business finance deals. Free to join, only pay a success fee on won deals.",
    path: "/lender/signup",
  });
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const ref = sp.get("ref") || "";
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    company: "", name: "", email: "", phone: "", password: "",
    panel_products: [], notes: "", ref,
  });
  useEffect(() => { if (ref) setForm((f) => ({ ...f, ref })); }, [ref]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (p) => set("panel_products",
    form.panel_products.includes(p) ? form.panel_products.filter((x) => x !== p) : [...form.panel_products, p]);

  const submit = async () => {
    if (!form.company || !form.name || !form.email || !form.password) return toast.error("Please complete all required fields.");
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    setBusy(true);
    try {
      await api.post("/lenders/register", form);
      setDone(true);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="bg-white text-ink min-h-screen">
        <Navbar />
        <div className="max-w-2xl mx-auto px-5 md:px-8 py-24 text-center">
          <div className="inline-flex h-16 w-16 rounded-2xl bg-emerald text-white items-center justify-center mb-6">
            <ShieldCheck className="h-8 w-8" strokeWidth={2.4} />
          </div>
          <h1 className="font-display font-extrabold text-[44px] md:text-[56px] leading-[1] tracking-[-0.035em] text-navy mb-6">
            You&apos;re in the queue.
          </h1>
          <p className="text-[16px] text-navy/70 leading-relaxed mb-10 font-medium">
            Thanks {form.name}. Our panel team reviews new lenders within one working day. You&apos;ll get an email as soon as {form.company} is approved and can start bidding on live deals.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-navy text-white font-extrabold text-[14px] hover:bg-navy-2">
            Back to home <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-white text-ink min-h-screen">
      <Navbar />
      <div className="max-w-3xl mx-auto px-5 md:px-8 py-14 md:py-20">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-soft text-emerald-3 font-mono text-[11px] uppercase tracking-[0.12em] font-bold mb-5">
          <Building2 className="h-3.5 w-3.5" strokeWidth={2.6} /> Lender panel
        </div>
        <h1 className="font-display font-extrabold text-[44px] md:text-[60px] leading-[0.98] tracking-[-0.035em] text-navy mb-4">
          Join the <span className="text-emerald">SwiftCash panel.</span>
        </h1>
        <p className="text-[16px] text-navy/70 mb-10 max-w-lg leading-[1.55] font-medium">
          Get instant access to £5k–£500M pre-qualified UK deal flow. Bid in real time, win the deals that fit your book.
        </p>

        {ref && (
          <div className="mb-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-soft border border-emerald/30 text-emerald-3 font-mono text-[11px] uppercase tracking-[0.12em] font-bold" data-testid="lender-referral-banner">
            <Sparkles className="h-3.5 w-3.5" strokeWidth={2.6} /> Referred by panel member · code {ref}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-5">
          <Field label="Lender / broker company"><Input value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="lender-company" className="h-12 rounded-xl font-medium" /></Field>
          <Field label="Contact name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="lender-name" className="h-12 rounded-xl font-medium" /></Field>
          <Field label="Business email"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="lender-email" className="h-12 rounded-xl font-medium" /></Field>
          <Field label="Phone (optional)"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="lender-phone" className="h-12 rounded-xl font-medium" /></Field>
          <Field label="Password" hint="Min 8 characters" className="md:col-span-2"><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="lender-password" className="h-12 rounded-xl font-medium" /></Field>
        </div>

        <div className="mt-8">
          <Label className="font-mono text-[11px] uppercase tracking-[0.12em] font-bold text-ink/70 mb-3 block">Panel products you fund</Label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
            {PRODUCTS.map((p) => {
              const on = form.panel_products.includes(p);
              return (
                <label key={p} data-testid={`lender-product-${p.replace(/\s/g, "-").toLowerCase()}`}
                  className={`cursor-pointer rounded-xl px-4 py-3 border font-bold text-[13.5px] flex items-center gap-2.5 transition-all
                    ${on ? "bg-ink text-white border-ink" : "bg-white border-line text-ink hover:border-ink/40 hover:bg-paper-warm"}`}>
                  <Checkbox checked={on} onCheckedChange={() => toggle(p)} />
                  {p}
                </label>
              );
            })}
          </div>
        </div>

        <div className="mt-8">
          <Field label="Notes (optional)"><Input value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Preferred deal size, sectors, ticket range…" data-testid="lender-notes" className="h-12 rounded-xl font-medium" /></Field>
        </div>

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-10 pt-8 border-t border-line">
          <div className="text-[13px] text-ink/60 font-medium">
            Already a lender? <Link to="/lender/login" className="text-emerald-3 font-bold hover:underline">Log in</Link>
          </div>
          <button onClick={submit} disabled={busy} data-testid="lender-submit"
            className="btn-shine group inline-flex items-center gap-2 h-12 px-7 rounded-full bg-emerald text-white hover:bg-emerald-2 text-[14px] font-extrabold disabled:opacity-50 shadow-emerald">
            {busy ? "Submitting…" : <>Apply to join panel <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={2.6} /></>}
          </button>
        </div>
      </div>
      <Footer />
    </div>
  );
}

function Field({ label, hint, children, className = "" }) {
  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-2">
        <Label className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-ink/60">{label}</Label>
        {hint && <span className="font-mono text-[10.5px] text-ink/35">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
